import { partition, sortBy } from "@rauricoste/core";
import { SenderWithQueue } from "./utils/SenderWithQueue";
import { TransportEvent, TransportEventSent } from "./TransportEvent";
import { SingleCallAsync } from "./utils/SingleCallAsync";

export type EventSyncStatus = "synced" | "sending" | "error" | "downloading";

export type EventSyncInterface<Event> = {
  getNewEventsFrom: (timestamp: number) => Promise<TransportEvent<Event>[]>;
  sendEvents: (events: TransportEventSent<Event>[]) => Promise<void>;
  delete(): Promise<void>;
};

export class EventSync<Event> {
  private getter: SingleCallAsync;
  private sender: SenderWithQueue<TransportEventSent<Event>>;
  public isUpToDate: boolean;

  constructor(
    public readonly syncInterface: EventSyncInterface<Event>,
    public readonly onEvent?: (event: Event) => void,
    public lastTimestamp = 0
  ) {
    this.isUpToDate = false;
    this.getter = new SingleCallAsync(async () => {
      const { lastTimestamp, onEvent } = this;
      const newEvents = await this.syncInterface.getNewEventsFrom(
        lastTimestamp
      );
      const { passing: events, notPassing: oldUnexpectedEvents } = partition(
        newEvents,
        (event) => event.timestamp > lastTimestamp
      );
      if (oldUnexpectedEvents.length) {
        console.warn("ignoring these old events", oldUnexpectedEvents);
      }
      this.isUpToDate = !events.length;
      if (!events.length) return;
      const sortedEvents = sortBy(
        events,
        (event) => `${event.timestamp}-${event.eventId}`
      );
      console.log("receiving remote events", sortedEvents);
      this.lastTimestamp = sortedEvents[sortedEvents.length - 1].timestamp;

      if (onEvent) {
        sortedEvents.forEach((transportEvent) =>
          onEvent(transportEvent.payload)
        );
      }
    });
    this.sender = new SenderWithQueue<TransportEventSent<Event>>(
      async (events) => {
        await this.syncInterface.sendEvents(events);
      }
    );
  }

  async getNewEvents(): Promise<void> {
    await this.getter.call();
  }

  send(event: Event) {
    const transportEvent = this.buildTransportEvent(event);
    this.sender.send(transportEvent);
    return { transportEvent };
  }

  buildTransportEvent(event: Event): TransportEventSent<Event> {
    return {
      eventId: Math.random().toString().substring(2),
      payload: event,
    };
  }

  getSyncStatus(): EventSyncStatus {
    if (
      !this.sender.singleSend.isLastCallSuccessful ||
      !this.getter.isLastCallSuccessful
    ) {
      return "error";
    }
    if (this.sender.toBeSent.length) return "sending";
    if (!this.isUpToDate) return "downloading";
    return "synced";
  }
}
